import { useState, useEffect, useCallback, createContext, useContext } from 'react';
import { getSessionId } from './useSessionTracking';
import { registerIntentSignalHandler } from './useIntentTracking';
import { getScoreBand } from '@/lib/kpDecisionEngine';
import { hasConsent } from '@/lib/consent';

const SESSION_INTEL_KEY = 'klanert-session-intel';
const RETURNING_VISITOR_KEY = 'klanert-returning-visitor';

export type IntentActionType = 
  | 'click_call' 
  | 'click_webinar' 
  | 'click_waitlist' 
  | 'click_whatsapp'
  | 'click_contact'
  | 'click_sleep_scan'
  | 'click_podcast'
  | 'click_download'
  | 'cta_click';

export interface SessionIntelligence {
  // Core metrics
  sessionId: string;
  totalPageViews: number;
  totalSessionTime: number; // in seconds
  isReturningVisitor: boolean;
  hasIntentSignal: boolean;
  intentActions: IntentActionType[];
  
  // Device info
  isDesktop: boolean;
  isMobile: boolean;
  isTablet: boolean;
  
  // Time-based
  visitDayOfWeek: number; // 0 = Sunday, 6 = Saturday
  visitHour: number; // 0-23
  visitDayName: string;
  
  // Entry source
  entrySource: string;
  
  // Engagement Score
  engagementScore: number;
  scoreBreakdown: {
    returningVisitor: number;
    pageViews: number;
    sessionTime: number;
    clickCall: number;
    desktop: number;
  };
  
  // Derived insights
  isEngaged: boolean;
  isHighIntent: boolean;
  sessionStartTime: number;
  
  // Actions
  incrementPageViews: () => void;
  setIntentSignal: (actionType?: IntentActionType) => void;
  getSessionDuration: () => number;
}

interface StoredIntelligence {
  totalPageViews: number;
  hasIntentSignal: boolean;
  intentActions: IntentActionType[];
  sessionStartTime: number;
  lastActivity: number;
  entrySource: string;
}

const defaultIntelligence: StoredIntelligence = {
  totalPageViews: 0,
  intentActions: [],
  hasIntentSignal: false,
  sessionStartTime: Date.now(),
  lastActivity: Date.now(),
  entrySource: 'direct',
};

// Extend Window interface for global access
declare global {
  interface Window {
    kp_intel: {
      entry_source: string;
      device_type: string;
      visit_day_of_week: number;
      visit_hour: number;
      is_returning: boolean;
      session_id: string;
    };
    kp_score: number;
    kp_score_band: 'low' | 'medium' | 'high';
  }
}

const getDeviceType = (): 'desktop' | 'mobile' | 'tablet' => {
  if (typeof window === 'undefined') return 'desktop';
  const ua = navigator.userAgent.toLowerCase();
  if (/tablet|ipad|playbook|silk/i.test(ua)) return 'tablet';
  if (/mobile|iphone|ipod|android|blackberry|opera mini|iemobile/i.test(ua)) return 'mobile';
  return 'desktop';
};

const getDayName = (dayIndex: number): string => {
  const days = ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'];
  return days[dayIndex];
};

const getEntrySource = (): string => {
  if (typeof window === 'undefined') return 'direct';
  
  const params = new URLSearchParams(window.location.search);
  const utmSource = params.get('utm_source');
  if (utmSource) return utmSource.toLowerCase();
  
  const referrer = document.referrer.toLowerCase();
  if (referrer.includes('linkedin')) return 'linkedin';
  if (referrer.includes('podcast')) return 'podcast';
  if (referrer.includes('google')) return 'google';
  if (referrer.includes('facebook')) return 'facebook';
  if (referrer.includes('twitter') || referrer.includes('x.com')) return 'twitter';
  if (referrer) return 'referral';
  
  return 'direct';
};

const loadStoredIntelligence = (): StoredIntelligence => {
  if (!hasConsent('statistics')) {
    return { ...defaultIntelligence, sessionStartTime: Date.now(), lastActivity: Date.now(), entrySource: 'direct' };
  }
  try {
    const stored = localStorage.getItem(SESSION_INTEL_KEY);
    if (stored) {
      const parsed = JSON.parse(stored) as Partial<StoredIntelligence>;
      const merged: StoredIntelligence = {
        ...defaultIntelligence,
        ...parsed,
        totalPageViews: typeof parsed.totalPageViews === 'number' ? parsed.totalPageViews : 0,
        hasIntentSignal: typeof parsed.hasIntentSignal === 'boolean' ? parsed.hasIntentSignal : false,
        intentActions: Array.isArray(parsed.intentActions) ? parsed.intentActions : [],
        sessionStartTime: typeof parsed.sessionStartTime === 'number' ? parsed.sessionStartTime : Date.now(),
        lastActivity: typeof parsed.lastActivity === 'number' ? parsed.lastActivity : Date.now(),
        entrySource: parsed.entrySource || getEntrySource(),
      };
      saveIntelligence(merged);
      return merged;
    }
  } catch {
    // Ignore
  }
  return { 
    ...defaultIntelligence, 
    sessionStartTime: Date.now(), 
    lastActivity: Date.now(),
    entrySource: getEntrySource(),
  };
};

const saveIntelligence = (intel: StoredIntelligence): void => {
  if (!hasConsent('statistics')) return;
  try {
    localStorage.setItem(SESSION_INTEL_KEY, JSON.stringify(intel));
  } catch {
    // Ignore
  }
};

const checkReturningVisitor = (): boolean => {
  if (!hasConsent('statistics')) return false;
  try {
    const hasVisited = localStorage.getItem(RETURNING_VISITOR_KEY);
    if (!hasVisited) {
      localStorage.setItem(RETURNING_VISITOR_KEY, 'true');
      return false;
    }
    return true;
  } catch {
    return false;
  }
};

/**
 * Expose intel and score to window for global access
 */
const exposeToWindow = (intel: SessionIntelligence): void => {
  if (typeof window === 'undefined') return;
  
  window.kp_intel = {
    entry_source: intel.entrySource,
    device_type: intel.isDesktop ? 'desktop' : intel.isMobile ? 'mobile' : 'tablet',
    visit_day_of_week: intel.visitDayOfWeek,
    visit_hour: intel.visitHour,
    is_returning: intel.isReturningVisitor,
    session_id: intel.sessionId,
  };
  
  window.kp_score = intel.engagementScore;
  window.kp_score_band = getScoreBand(intel.engagementScore);
};

// Context for global access
const SessionIntelligenceContext = createContext<SessionIntelligence | null>(null);

export const useSessionIntelligence = (): SessionIntelligence => {
  const context = useContext(SessionIntelligenceContext);
  if (context) return context;
  return useSessionIntelligenceCore();
};

export const useSessionIntelligenceCore = (): SessionIntelligence => {
  const sessionId = getSessionId();
  const now = new Date();
  const deviceType = getDeviceType();
  
  const [intel, setIntel] = useState<StoredIntelligence>(() => loadStoredIntelligence());
  const [isReturningVisitor] = useState(() => checkReturningVisitor());
  
  useEffect(() => {
    const interval = setInterval(() => {
      setIntel(prev => {
        const updated = { ...prev, lastActivity: Date.now() };
        saveIntelligence(updated);
        return updated;
      });
    }, 10000);
    return () => clearInterval(interval);
  }, []);

  const incrementPageViews = useCallback(() => {
    setIntel(prev => {
      const updated = { 
        ...prev, 
        totalPageViews: prev.totalPageViews + 1,
        lastActivity: Date.now(),
      };
      saveIntelligence(updated);
      return updated;
    });
  }, []);

  const setIntentSignal = useCallback((actionType?: IntentActionType) => {
    setIntel(prev => {
      const currentActions = Array.isArray(prev.intentActions) ? prev.intentActions : [];
      const newActions = actionType && !currentActions.includes(actionType)
        ? [...currentActions, actionType]
        : currentActions;
      
      const updated = { 
        ...prev, 
        hasIntentSignal: true, 
        intentActions: newActions,
        lastActivity: Date.now() 
      };
      saveIntelligence(updated);
      return updated;
    });
  }, []);

  const getSessionDuration = useCallback(() => {
    return Math.round((Date.now() - intel.sessionStartTime) / 1000);
  }, [intel.sessionStartTime]);

  const totalSessionTime = Math.round((intel.lastActivity - intel.sessionStartTime) / 1000);
  const safeIntentActions = Array.isArray(intel.intentActions) ? intel.intentActions : [];
  
  const scoreBreakdown = {
    returningVisitor: isReturningVisitor ? 2 : 0,
    pageViews: intel.totalPageViews > 2 ? 2 : 0,
    sessionTime: totalSessionTime > 90 ? 2 : 0,
    clickCall: safeIntentActions.includes('click_call') ? 3 : 0,
    desktop: deviceType === 'desktop' ? 1 : 0,
  };
  
  const engagementScore = !hasConsent('statistics') ? 0 :
    scoreBreakdown.returningVisitor +
    scoreBreakdown.pageViews +
    scoreBreakdown.sessionTime +
    scoreBreakdown.clickCall +
    scoreBreakdown.desktop;

  const intelligence: SessionIntelligence = {
    sessionId,
    totalPageViews: intel.totalPageViews,
    totalSessionTime,
    isReturningVisitor,
    hasIntentSignal: intel.hasIntentSignal || false,
    intentActions: safeIntentActions,
    isDesktop: deviceType === 'desktop',
    isMobile: deviceType === 'mobile',
    isTablet: deviceType === 'tablet',
    visitDayOfWeek: now.getDay(),
    visitHour: now.getHours(),
    visitDayName: getDayName(now.getDay()),
    entrySource: intel.entrySource || 'direct',
    engagementScore,
    scoreBreakdown,
    isEngaged: intel.totalPageViews > 2 || totalSessionTime > 60,
    isHighIntent: intel.hasIntentSignal,
    sessionStartTime: intel.sessionStartTime,
    incrementPageViews,
    setIntentSignal,
    getSessionDuration,
  };

  // Expose to window on every update
  useEffect(() => {
    exposeToWindow(intelligence);
  }, [engagementScore, intel.entrySource]);

  return intelligence;
};

// Provider component
export const SessionIntelligenceProvider = ({ children }: { children: React.ReactNode }) => {
  const intelligence = useSessionIntelligenceCore();
  
  useEffect(() => {
    registerIntentSignalHandler(intelligence.setIntentSignal);
  }, [intelligence.setIntentSignal]);
  
  return (
    <SessionIntelligenceContext.Provider value={intelligence}>
      {children}
    </SessionIntelligenceContext.Provider>
  );
};

// Global access function (for non-React code)
export const getSessionIntelligence = (): Omit<SessionIntelligence, 'incrementPageViews' | 'setIntentSignal' | 'getSessionDuration'> => {
  const sessionId = getSessionId();
  const intel = loadStoredIntelligence();
  const isReturningVisitor = checkReturningVisitor();
  const now = new Date();
  const totalSessionTime = Math.round((intel.lastActivity - intel.sessionStartTime) / 1000);
  const deviceType = getDeviceType();
  const safeIntentActions = Array.isArray(intel.intentActions) ? intel.intentActions : [];
  
  const scoreBreakdown = {
    returningVisitor: isReturningVisitor ? 2 : 0,
    pageViews: intel.totalPageViews > 2 ? 2 : 0,
    sessionTime: totalSessionTime > 90 ? 2 : 0,
    clickCall: safeIntentActions.includes('click_call') ? 3 : 0,
    desktop: deviceType === 'desktop' ? 1 : 0,
  };
  
  const engagementScore = !hasConsent('statistics') ? 0 :
    scoreBreakdown.returningVisitor +
    scoreBreakdown.pageViews +
    scoreBreakdown.sessionTime +
    scoreBreakdown.clickCall +
    scoreBreakdown.desktop;

  return {
    sessionId,
    totalPageViews: intel.totalPageViews || 0,
    totalSessionTime,
    isReturningVisitor,
    hasIntentSignal: intel.hasIntentSignal || false,
    intentActions: safeIntentActions,
    isDesktop: deviceType === 'desktop',
    isMobile: deviceType === 'mobile',
    isTablet: deviceType === 'tablet',
    visitDayOfWeek: now.getDay(),
    visitHour: now.getHours(),
    visitDayName: getDayName(now.getDay()),
    entrySource: intel.entrySource || 'direct',
    engagementScore,
    scoreBreakdown,
    isEngaged: (intel.totalPageViews || 0) > 2 || totalSessionTime > 60,
    isHighIntent: intel.hasIntentSignal || false,
    sessionStartTime: intel.sessionStartTime,
  };
};

// Expose to window for debugging
if (typeof window !== 'undefined') {
  (window as any).__getSessionIntelligence = getSessionIntelligence;
}

export default useSessionIntelligence;
